import { useToast } from "@aircall/tractor";
import { NotFoundError } from "@dashboard/library";
import { useTranslation } from "react-i18next";
import {
  GetCallQuery,
  GetCallQueryVariables,
} from "@/graphql/generated/graphql";
import { GetCall } from "@/graphql/queries/GetCall";
import { useHasPredictedCsat } from "@/hooks/useHasPredictedCsat/useHasPredictedCsat";
import { useHasSentimentAnalysis } from "@/hooks/useHasSentimentAnalysis/useHasSentimentAnalysis";
import { useBaseStore } from "@/store/store";
import { mapToCall } from "../../states/call/call.utility";
import { useQueryWrapper } from "../useQueryWrapper/useQueryWrapper";
import { UseCallByIdQueryReturnType } from "./useCallByIdQuery.decl";
/**
 * The hook responsible for querying from server the call data and map it to the Call entity.
 * @param callId - id of call
 */
export function useCallByIdQuery(callId: string): UseCallByIdQueryReturnType {
  const { t } = useTranslation();

  const hasSentimentAnalysis = useHasSentimentAnalysis();
  const hasPredictedCsat = useHasPredictedCsat();
  const { showToast } = useToast();
  const setCall = useBaseStore((store) => store.setCall);

  const { data, loading, error } = useQueryWrapper<
    GetCallQuery,
    GetCallQueryVariables
  >(GetCall, {
    // We intentionally don't let the wrapper throw on every error. With `errorPolicy: "all"`
    // the backend can return a usable `getCall` alongside GraphQL errors on sub-fields
    // (e.g. transcription insights). In that case we want the page to render with partial
    // data instead of crashing the whole page. We only fail hard below when nothing usable
    // came back. [CI-6014]
    shouldThrowError: false,
    // The transcript (utterances -> per-word entries) is large. With a normalizing cache
    // Apollo writes tens of thousands of nodes into InMemoryCache on the main thread,
    // freezing the tab on long calls. The result is consumed from the Zustand store
    // (setCall below) and never read back from the cache, so skip the cache write. See DAT-455.
    fetchPolicy: "no-cache",
    variables: {
      callId,
      skipSentiments: !hasSentimentAnalysis,
      skipPredictedCsat: !hasPredictedCsat,
    },
    errorPolicy: "all",
    onCompleted: (data) => {
      if (
        !data?.getCall ||
        data.getCall.__typename === "ResourceNotFoundException"
      ) {
        return;
      }
      const call = mapToCall(data.getCall);
      setCall(call);
    },
    onError: () =>
      showToast({
        variant: "critical",
        message: t("toast.fetch_failure"),
      }),
  });

  // Only a fatal error (nothing usable resolved) should crash the page. A partial error
  // still returns `getCall`, so we keep rendering. This mirrors the fatal-error handling
  // that previously lived in the query wrapper. [CI-6014]
  if (data?.getCall?.__typename === "ResourceNotFoundException") {
    throw new NotFoundError("Page not found.");
  }

  if (error && !data?.getCall) {
    if (error.message.includes("404")) {
      throw new NotFoundError("Page not found.");
    }
    throw error;
  }

  return {
    call: data?.getCall,
    loading,
    error,
  };
}
